/**
 * Credit Purchase Page
 *
 * Allows users to purchase credits for PAYG usage.
 * Credit rules are defined in /docs/credits.md.
 */

import * as React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCurrency } from "@/context/CurrencyContext";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  Coins,
  Check,
  ArrowLeft,
  Sparkles,
  TrendingDown,
  X,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CreditBalanceDisplay } from "@/components/credits/CreditBalanceDisplay";
import { CREDIT_COSTS, formatCredits } from "@/types/credits";
import Sidebar from "@/components/layout/Sidebar";
import TopBar from "@/components/layout/TopBar";
import { paymentService, SubscriptionPlan } from "@/services/paymentService";
import { useToast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CreditCard } from "lucide-react";
import PaymentGatewaySelection from "@/components/payment/PaymentGatewaySelection";
import { useState, useRef } from "react";

// Credit pricing structure (ZAR)
// R44.85 per credit PAYG rate, bulk pricing from R13.50 per credit at 10+
// Only 5, 10, 15, 20 credits can be purchased
const CREDIT_PRICING: Record<number, number> = {
  1: 44.85,
  2: 82.5,
  3: 112.5,
  4: 139.2,
  5: 150.0,
  6: 172.8,
  7: 195.3,
  8: 217.2,
  9: 240.3,
  10: import.meta.env.DEV ? 14.0 : 135.0,
  11: 148.5,
  12: 162.0,
  13: 175.5,
  14: 189.0,
  15: 202.5,
  16: 216.0,
  17: 229.5,
  18: 243.0,
  19: 256.5,
  20: 270.0,
};

const getEffectivePricePerCredit = (credits: number): number => {
  const totalPrice = CREDIT_PRICING[credits];
  return totalPrice / credits;
};

// Featured packages for quick selection
const FEATURED_PACKAGES = [
  { id: "credits-5", credits: 5, highlight: "Great value", popular: false },
  { id: "credits-10", credits: 10, highlight: "Best value", popular: true },
  { id: "credits-15", credits: 15, highlight: "Power user", popular: false },
  { id: "credits-20", credits: 20, highlight: "20 credits", popular: false },
  // Dev-only test credit package
  ...(import.meta.env.DEV
    ? [
        {
          id: "credits-test",
          credits: 10,
          highlight: "Test credit (dev only)",
          popular: false,
          testPrice: 14.0,
        },
      ]
    : []),
];

export function PurchaseCreditsPage() {
  // Ref for price display region
  const priceDisplayRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useSupabaseAuth();
  const { toast } = useToast();
  const { currency, formatPrice } = useCurrency();
  const [showCurrencyTip, setShowCurrencyTip] = useState(true);
  const [purchasing, setPurchasing] = React.useState<string | null>(null);
  const [selectedCredits, setSelectedCredits] = React.useState<number>(10);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [currentCreditPlan, setCurrentCreditPlan] =
    useState<SubscriptionPlan | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Redirect if not authenticated
  React.useEffect(() => {
    if (!authLoading && !user) {
      navigate("/", { replace: true, state: { showAuthDialog: true } });
    }
  }, [user, authLoading, navigate]);

  const handlePurchase = async (credits: number) => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to purchase credits.",
        variant: "destructive",
      });
      return;
    }

    // Special dev-only test credit plan
    const isTestCredit =
      import.meta.env.DEV &&
      credits === 10 &&
      FEATURED_PACKAGES.some((pkg) => pkg.id === "credits-test");
    const creditPlan: SubscriptionPlan = {
      id: isTestCredit ? "credits-test" : `credits-${credits}`,
      name: isTestCredit ? "Test Credit (Dev Only)" : `${credits} Credits`,
      price: isTestCredit ? 14.0 : CREDIT_PRICING[credits],
      currency: "ZAR",
      interval: "month", // Using 'month' as workaround for type, logically it's one-time
      features: [
        isTestCredit
          ? "10 test credits for development (dev only)"
          : `${credits} credits for My!hu`,
      ],
    };

    setCurrentCreditPlan(creditPlan);
    setShowPaymentDialog(true);
  };

  const handlePaymentInitiated = (sessionId: string, redirectUrl?: string) => {
    setShowPaymentDialog(false);

    // Store session info
    if (typeof window !== "undefined") {
      localStorage.setItem("pendingPaymentSession", sessionId);
    }

    toast({
      title: "Payment Initiated",
      description: "Redirecting to payment provider...",
    });

    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#40e0d0]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-gradient-to-b from-background to-background/80">
      <Sidebar />

      <div className="flex-1 flex flex-col">
        <TopBar
          title="Purchase Credits"
          icon={<Coins className="h-4 w-4 text-[#40e0d0]" />}
          showBackButton={true}
        />

        <main className="flex-1 container mx-auto px-4 py-8 max-w-5xl">
          {/* Back button */}

          {/* Currency tip banner */}
          {showCurrencyTip && currency === "ZAR" && (
            <div
              className="running-glow-border mb-6"
              style={{ "--glow-bg": "rgba(0,0,0,0.9)" } as React.CSSProperties}
            >
              <Alert className="border-0 bg-transparent text-background dark:text-foreground font-semibold flex items-center justify-between">
                <AlertDescription className="flex-1">
                  Prices are shown in ZAR. You can switch to USD in the{" "}
                  <span className="font-bold text-teal-500">user menu</span>{" "}
                  <i>(top-right corner).</i>
                </AlertDescription>
                <button
                  onClick={() => setShowCurrencyTip(false)}
                  className="ml-4 shrink-0 rounded-sm opacity-70 hover:opacity-100 focus:outline-none"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </Alert>
            </div>
          )}

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Purchase Credits
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Credits are used when you generate reports, predictions, or
              exports. Pay only for what you use — no subscriptions, no time
              limits.
            </p>
          </div>

          {/* Current balance */}
          <div className="flex justify-center mb-8">
            <CreditBalanceDisplay userId={user.id} showHistory={true} />
          </div>

          {/* Credit slider selector */}
          <Card className="mb-8">
            <CardHeader className="text-center">
              <CardTitle>Select Credits</CardTitle>
              <CardDescription>
                Slide to choose how many credits you want to purchase
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Slider */}
              <div className="px-4">
                <Slider
                  value={[selectedCredits]}
                  onValueChange={(value) => {
                    // Snap slider value to the nearest allowed option (5, 10, 15, 20)
                    const allowed = [5, 10, 15, 20];
                    const v = value[0];
                    let nearest = allowed[0];
                    let bestDiff = Math.abs(v - nearest);
                    for (let i = 1; i < allowed.length; i++) {
                      const d = Math.abs(v - allowed[i]);
                      if (d < bestDiff) {
                        bestDiff = d;
                        nearest = allowed[i];
                      }
                    }
                    setSelectedCredits(nearest);
                  }}
                  min={5}
                  max={20}
                  step={5}
                  className="w-full"
                />
                {/* Visual ticks aligned precisely to slider positions */}
                <div className="relative mt-2 h-6">
                  {([5, 10, 15, 20] as number[]).map((val) => {
                    const left = ((val - 5) / (20 - 5)) * 100;
                    return (
                      <div
                        key={val}
                        style={{ left: `${left}%` }}
                        className="absolute top-0 transform -translate-x-1/2 text-xs text-muted-foreground flex flex-col items-center"
                      >
                        <div className="w-px h-2 bg-border mb-1" />
                        <span>{val}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Selected price display */}
              <div
                ref={priceDisplayRef}
                className="text-center p-6 rounded-lg bg-muted/50 border"
              >
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Coins className="h-8 w-8 text-[#40e0d0]" />
                  <span className="text-5xl font-bold">{selectedCredits}</span>
                </div>
                <span className="text-muted-foreground">credits</span>

                <div className="mt-4 space-y-1">
                  <div className="text-3xl font-bold">
                    {formatPrice(CREDIT_PRICING[selectedCredits])}
                  </div>
                  <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                    {formatPrice(getEffectivePricePerCredit(selectedCredits))}{" "}
                    per credit
                    {selectedCredits >= 10 && (
                      <Badge
                        variant="secondary"
                        className="ml-2 bg-green-500/20 text-green-600"
                      >
                        <TrendingDown className="h-3 w-3 mr-1" />
                        Bulk pricing
                      </Badge>
                    )}
                  </div>
                </div>

                <Button
                  size="lg"
                  className="mt-6 bg-[#40e0d0] text-black hover:bg-[#40e0d0]/90"
                  onClick={() => handlePurchase(selectedCredits)}
                  disabled={!!purchasing}
                >
                  {purchasing === `credits-${selectedCredits}`
                    ? "Processing..."
                    : `Purchase ${selectedCredits} Credit${selectedCredits > 1 ? "s" : ""}`}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Featured quick-select packages */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {FEATURED_PACKAGES.map((pkg) => {
              const price = CREDIT_PRICING[pkg.credits];
              const effectivePrice = getEffectivePricePerCredit(pkg.credits);

              return (
                <Card
                  key={pkg.id}
                  className={`relative cursor-pointer transition-all hover:scale-105 ${
                    pkg.popular
                      ? "border-[#40e0d0] shadow-lg shadow-[#40e0d0]/10"
                      : ""
                  } ${selectedCredits === pkg.credits ? "ring-2 ring-[#40e0d0]" : ""}`}
                  onClick={() => setSelectedCredits(pkg.credits)}
                >
                  {pkg.popular && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#40e0d0] text-black">
                      <Sparkles className="h-3 w-3 mr-1" />
                      Best Value
                    </Badge>
                  )}
                  <CardContent className="pt-6 text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Coins className="h-4 w-4 text-[#40e0d0]" />
                      <span className="text-2xl font-bold">{pkg.credits}</span>
                    </div>
                    <div className="text-lg font-semibold">
                      {formatPrice(price)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatPrice(effectivePrice)}/credit
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {pkg.highlight}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Full pricing table */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-lg">Full Pricing Table</CardTitle>
              <CardDescription>
                Bulk pricing from {formatPrice(13.5)} per credit when you buy 10
                or more
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3">Credits</th>
                      <th className="text-right py-2 px-3">Total Price</th>
                      <th className="text-right py-2 px-3">Price/Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(CREDIT_PRICING).map(([credits, price]) => {
                      const numCredits = parseInt(credits);
                      const effectivePrice = price / numCredits;
                      const isSelected = selectedCredits === numCredits;
                      const isBestRate = numCredits >= 10;

                      return (
                        <tr
                          key={credits}
                          className={`border-b cursor-pointer hover:bg-muted/50 transition-colors ${
                            isSelected ? "bg-[#40e0d0]/10" : ""
                          } ${isBestRate ? "bg-green-500/5" : ""}`}
                          onClick={() => {
                            setSelectedCredits(numCredits);
                            // Scroll to price display
                            setTimeout(() => {
                              priceDisplayRef.current?.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                              });
                            }, 0);
                          }}
                        >
                          <td className="py-2 px-3 font-medium">
                            <div className="flex items-center gap-2">
                              {numCredits}
                              {numCredits === 10 && (
                                <Badge
                                  variant="secondary"
                                  className="text-xs bg-green-500/20 text-green-600"
                                >
                                  Bulk pricing starts here
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-3 text-right">
                            {formatPrice(price)}
                          </td>
                          <td className="py-2 px-3 text-right font-semibold">
                            {formatPrice(effectivePrice)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground text-center mt-4">
                If you use My!hu regularly, the{" "}
                <Link
                  to="/professional"
                  className="text-[#40e0d0] hover:underline"
                >
                  Professional plan
                </Link>{" "}
                may be more cost-effective.
              </p>
            </CardContent>
          </Card>

          {/* Credit costs reference */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-lg">What Credits Can Do</CardTitle>
              <CardDescription>
                Credits are deducted only after successful output generation. No
                output = no credit deduction. All outputs you generate are saved
                and remain accessible to you.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <span>Reports</span>
                  <Badge variant="outline">
                    {formatCredits(CREDIT_COSTS.STANDARD_REPORT)}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <span>Guest invite</span>
                  <Badge variant="outline">
                    {formatCredits(CREDIT_COSTS.GUEST_INVITE)}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <span>5- 10 Year Forecast</span>
                  <Badge variant="outline">
                    {formatCredits(CREDIT_COSTS.FIVE_YEAR_FORECAST)}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <span>Dataset Export</span>
                  <Badge variant="outline">
                    {formatCredits(CREDIT_COSTS.DATASET_EXPORT)}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="mt-8 text-center text-sm text-muted-foreground">
            <p className="flex items-center justify-center gap-2">
              <Sparkles className="h-4 w-4 text-[#40e0d0]" />
              Need a custom bulk package?{" "}
              <Link to="/contact" className="underline hover:text-foreground">
                Contact sales
              </Link>
            </p>
          </div>
        </main>

        {/* Payment Gateway Selection Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Complete Purchase
              </DialogTitle>
              <DialogDescription className="sr-only">
                Select your preferred payment method to purchase credits
              </DialogDescription>
            </DialogHeader>
            {currentCreditPlan && (
              <PaymentGatewaySelection
                plan={currentCreditPlan}
                onPaymentInitiated={handlePaymentInitiated}
                isProcessing={isProcessing}
                setIsProcessing={setIsProcessing}
                onCloseDialog={() => setShowPaymentDialog(false)}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default PurchaseCreditsPage;
